import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, CheckCircle2, Copy, FileCheck, Hash, ShieldCheck, UploadCloud, XCircle } from "lucide-react";
import { formatBytes } from "../../util";

export function FileChecksum() {
  const [file, setFile] = useState<File | null>(null);
  const [sha256Hash, setSha256Hash] = useState<string | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [verifyInput, setVerifyInput] = useState("");
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const calculateHash = async (f: File) => {
    setFile(f);
    setSha256Hash(null);
    setCalculating(true);
    setProgress(0);

    try {
      const buffer = await f.arrayBuffer();
      const digest = await crypto.subtle.digest("SHA-256", buffer);
      const hashArray = Array.from(new Uint8Array(digest));
      const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
      setSha256Hash(hashHex);
    } catch {
      alert("Could not read file into memory. Try a file smaller than 1 GB.");
    } finally {
      setCalculating(false);
    }
  };

  const copy = async () => {
    if (!sha256Hash) return;
    await navigator.clipboard.writeText(sha256Hash);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const cleanVerify = verifyInput.trim().toLowerCase();
  const isMatch = sha256Hash && cleanVerify ? sha256Hash.toLowerCase() === cleanVerify : null;

  return (
    <article className="panel legal" style={{ maxWidth: 780, margin: "0 auto" }}>
      <header style={{ marginBottom: 24 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", background: "rgba(0, 255, 102, 0.1)", borderRadius: 999, border: "1px solid rgba(0, 255, 102, 0.25)", fontSize: 12, color: "#7cff5c", fontFamily: "ui-monospace, monospace", marginBottom: 12 }}>
          <Hash size={12} /> Free Client-Side Tool
        </div>
        <h1 style={{ fontSize: "clamp(26px, 4vw, 36px)", lineHeight: 1.15, margin: "0 0 14px", fontWeight: 800 }}>
          Online SHA-256 Checksum Verifier
        </h1>
        <p className="hint" style={{ fontSize: 15, lineHeight: 1.6, margin: 0 }}>
          Calculate and verify cryptographic SHA-256 hashes directly in your browser. The file is processed locally in RAM; zero bytes are uploaded.
        </p>
      </header>

      {/* Dropzone */}
      <div
        className="dropzone"
        onClick={() => inputRef.current?.click()}
        style={{ marginBottom: 24 }}
      >
        <div className="dropzone-icon">
          <FileCheck size={28} />
        </div>
        <strong>Choose or drop a file to compute its SHA-256 hash</strong>
        <small>Processed entirely inside your browser's WebCrypto API</small>
        <input
          ref={inputRef}
          type="file"
          hidden
          onChange={(e) => {
            if (e.target.files?.[0]) calculateHash(e.target.files[0]);
            e.target.value = "";
          }}
        />
      </div>

      {calculating && (
        <div className="alert ok" style={{ textAlign: "center" }}>
          Computing SHA-256 cryptographic digest locally…
        </div>
      )}

      {file && sha256Hash && (
        <div style={{ padding: 20, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", marginBottom: 28 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontWeight: 600 }}>{file.name}</span>
            <span className="size">{formatBytes(file.size)}</span>
          </div>

          <label style={{ fontSize: 12, color: "var(--muted)", display: "block", marginBottom: 6 }}>
            SHA-256 HASH:
          </label>
          <div className="linkbox" style={{ marginTop: 0 }}>
            <input readOnly value={sha256Hash} onFocus={(e) => e.currentTarget.select()} style={{ color: "#7cff5c" }} />
            <button className="btn" onClick={copy}>
              {copied ? <Check size={16} /> : <Copy size={16} />}
              <span>{copied ? "Copied" : "Copy"}</span>
            </button>
          </div>

          {/* Verification Box */}
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
            <label style={{ fontSize: 13, color: "var(--text)", display: "block", marginBottom: 6 }}>
              Compare with expected hash:
            </label>
            <input
              type="text"
              placeholder="Paste hash here to verify integrity…"
              value={verifyInput}
              onChange={(e) => setVerifyInput(e.target.value)}
              className="textinput"
              style={{ marginTop: 0 }}
            />

            {isMatch === true && (
              <div className="alert ok" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                <CheckCircle2 size={16} color="#00ff66" />
                <b>Integrity verified!</b> The SHA-256 hashes match exactly.
              </div>
            )}
            {isMatch === false && (
              <div className="alert error" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                <XCircle size={16} color="#ff5c5c" />
                <b>Hash mismatch!</b> The file has been modified or corrupted.
              </div>
            )}
          </div>
        </div>
      )}

      <section style={{ marginBottom: 28 }}>
        <h2>What is a SHA-256 checksum?</h2>
        <p>
          A SHA-256 checksum is a 256-bit cryptographic fingerprint representing a file's exact binary contents. If even a single
          bit changes, the resulting hash changes completely (the avalanche effect). Software developers and security teams use
          checksums to verify that downloads have not been tampered with or corrupted in transit.
        </p>
      </section>

      <div style={{ padding: 22, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", textAlign: "center", marginTop: 28 }}>
        <h3 style={{ margin: "0 0 8px" }}>Send verified files with LimeShare</h3>
        <p className="hint" style={{ margin: "0 0 16px" }}>
          LimeShare checks the cryptographic integrity of every 4 MB chunk with AES-256-GCM authentication tags.
        </p>
        <Link to="/" className="btn" style={{ textDecoration: "none" }}>
          <UploadCloud size={16} /> Send Encrypted Files
        </Link>
      </div>
    </article>
  );
}
